import { api } from "./api";

/** Convert a base64url-encoded VAPID public key to a Uint8Array as required
 *  by the Web Push API's `applicationServerKey` parameter. */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function pushPermission() {
  return pushSupported() ? Notification.permission : "denied";
}

/** Ensure the service worker is registered; returns the registration. */
async function getReadyRegistration() {
  if (!navigator.serviceWorker) throw new Error("No service worker support");
  // The app registers /service-worker.js at boot in index.js; we just await it.
  return await navigator.serviceWorker.ready;
}

/** Subscribe (or return existing subscription) and persist on the backend.
 *  Resolves with the PushSubscription on success, throws on failure. */
export async function subscribeToPush() {
  if (!pushSupported()) throw new Error("push-unsupported");

  const perm = await Notification.requestPermission();
  if (perm !== "granted") {
    const e = new Error("permission-denied");
    e.code = "permission-denied";
    throw e;
  }

  const { data } = await api.get("/push/public-key");
  if (!data?.enabled || !data?.publicKey) {
    throw new Error("push-server-disabled");
  }

  const reg = await getReadyRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    });
  }

  // Persist to backend (idempotent upsert)
  const payload = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: payload.endpoint,
    keys: payload.keys,
    expirationTime: payload.expirationTime || null,
  });
  return sub;
}

export async function unsubscribeFromPush() {
  if (!pushSupported()) return false;
  const reg = await getReadyRegistration();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return false;
  try {
    await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
  } catch {}
  await sub.unsubscribe();
  return true;
}

export async function getCurrentSubscription() {
  if (!pushSupported()) return null;
  const reg = await getReadyRegistration();
  return await reg.pushManager.getSubscription();
}

export async function sendTestPush() {
  const r = await api.post("/push/test", {
    title: "HOSYS · Prova",
    body: "Les notificacions funcionen correctament 🎉",
    url: "/app",
  });
  return r.data;
}

export async function getPreferences() {
  const r = await api.get("/push/preferences");
  return r.data;
}

export async function updatePreferences(prefs) {
  const r = await api.put("/push/preferences", { prefs });
  return r.data;
}
